/**
 * career-compass-hub — the Kompass landing (route `/compass`): visual navigation to
 * the durable self-view. Five hero-image tiles — Meine Werte · Profil · Lebenslauf ·
 * Evidenzen · Unterlagen — each an `<a>` with a theme-aware hero background and a
 * title + one honest line. The hero paints from `/heroes/<name>.<theme>.webp`; a
 * missing file simply doesn't paint (the brand surface stays — graceful fallback,
 * never a broken-image icon). A quiet thread under the grid connects the arc
 * Kompass → Suche → Bewerbungen.
 */

import {
  KitElement,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type ViewModel,
} from "@platform/web-kit"
import { html, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, RouterToken } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { Router } from "@platform/web-kit"

interface HubTile {
  url: string
  /** The hero asset basename under `/heroes/<img>.<theme>.webp`. */
  img: string
  titleKey: CareerKey
  descKey: CareerKey
}

const TILES: HubTile[] = [
  { url: "/compass/werte", img: "werte", titleKey: "compass.werte.title", descKey: "compass.werte.desc" },
  { url: "/compass/profile", img: "profil", titleKey: "compass.profil.title", descKey: "compass.profile.desc" },
  { url: "/compass/cv", img: "lebenslauf", titleKey: "compass.lebenslauf.title", descKey: "compass.cv.desc" },
  { url: "/compass/evidence", img: "evidenzen", titleKey: "compass.evidenzen.title", descKey: "compass.evidence.desc" },
  { url: "/compass/assets", img: "assets", titleKey: "compass.unterlagen.title", descKey: "compass.assets.desc" },
]

export class CompassHubVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>> = []
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
  }

  attach(): () => void {
    return () => {}
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerCompassHub extends KitElement<CompassHubVm> {
  protected createViewModel(ctx: KitContext): CompassHubVm {
    return new CompassHubVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  private tile(vm: CompassHubVm, tile: HubTile): TemplateResult {
    const media = `--hero-l:url('/heroes/${tile.img}.light.webp');--hero-d:url('/heroes/${tile.img}.dark.webp')`
    return html`
      <a class="hero-tile" href=${tile.url} data-act="nav" data-url=${tile.url} lang="de">
        <span class="hero-tile-media" style=${media} aria-hidden="true"></span>
        <span class="hero-tile-body">
          <span class="hero-tile-title" lang="de">${vm.t(tile.titleKey)}</span>
          <span class="hero-tile-desc" lang="de">${vm.t(tile.descKey)}</span>
        </span>
      </a>
    `
  }

  protected render(vm: CompassHubVm): TemplateResult {
    return html`
      <section class="surface compass-hub" aria-labelledby="compass-title">
        <h1 id="compass-title" class="surface-title" lang="de">${vm.t("surface.kompass.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("compass.lead")}</p>
        <div class="hero-grid">${TILES.map((t) => this.tile(vm, t))}</div>
        <p class="compass-thread" lang="de">${vm.t("compass.thread")}</p>
      </section>
    `
  }

  protected override actions(vm: CompassHubVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
